import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar, TrendingUp, Package, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const VendorPromotions = () => {
  const { apiCall } = useAuth();
  const navigate = useNavigate();
  const [promotions, setPromotions] = useState([]);
  const [myOptIns, setMyOptIns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('available');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch available promotions (upcoming and live)
      const promoResponse = await apiCall('/api/marketing/promotions');
      setPromotions(promoResponse.data || []);
      
      // Fetch vendor's opt-ins
      const optInResponse = await apiCall('/api/marketing/vendor/promotion-opt-ins');
      setMyOptIns(optInResponse.data || []);
    } catch (error) {
      toast.error('Failed to load promotions');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Live':
        return 'bg-green-100 text-green-800';
      case 'Upcoming':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const isAlreadyOptedIn = (promotionId) => {
    return myOptIns.some(optIn => optIn.sale_id === promotionId && optIn.status === 'Active');
  };

  const availablePromotions = promotions.filter(p => !isAlreadyOptedIn(p.id));
  const myPromotions = promotions.filter(p => isAlreadyOptedIn(p.id));

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Platform Promotions</h1>
            <p className="text-gray-600 mt-2">Participate in platform-wide sales events</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Available Promotions</p>
                  <p className="text-2xl font-bold">{availablePromotions.length}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">My Participations</p>
                  <p className="text-2xl font-bold">{myPromotions.length}</p>
                </div>
                <Package className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Products Committed</p>
                  <p className="text-2xl font-bold">
                    {myOptIns.reduce((sum, opt) => sum + opt.total_products, 0)}
                  </p>
                </div>
                <Package className="h-8 w-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Card>
          <CardHeader>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="available">
                  Available ({availablePromotions.length})
                </TabsTrigger>
                <TabsTrigger value="participating">
                  My Participations ({myPromotions.length})
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center py-12 text-gray-500">Loading...</p>
            ) : activeTab === 'available' ? (
              availablePromotions.length === 0 ? (
                <div className="text-center py-12">
                  <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No promotions available at the moment</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {availablePromotions.map((promotion) => (
                    <Card key={promotion.id} className="hover:shadow-md transition-shadow border-l-4 border-l-blue-500">
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-3">
                              <h3 className="text-lg font-semibold">{promotion.name}</h3>
                              <Badge className={getStatusColor(promotion.status)}>
                                {promotion.status}
                              </Badge>
                              <Badge variant="outline">{promotion.theme}</Badge>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <Calendar className="h-4 w-4" />
                                <div>
                                  <p className="font-medium">Start Date</p>
                                  <p>{formatDate(promotion.start_timestamp)}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <Calendar className="h-4 w-4" />
                                <div>
                                  <p className="font-medium">End Date</p>
                                  <p>{formatDate(promotion.end_timestamp)}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <TrendingUp className="h-4 w-4" />
                                <div>
                                  <p className="font-medium">Min Discount Required</p>
                                  <p className="text-lg font-bold text-blue-600">{promotion.discount_mandate_min}%</p>
                                </div>
                              </div>
                            </div>

                            <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                              <p className="text-sm text-blue-800">
                                <strong>Requirements:</strong> You must offer at least {promotion.discount_mandate_min}% discount on participating products to join this promotion.
                              </p>
                            </div>
                          </div>
                          
                          <div className="ml-4">
                            <Button
                              onClick={() => navigate(`/promotions/${promotion.id}/opt-in`)}
                              className="whitespace-nowrap"
                            >
                              Opt In
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )
            ) : (
              myPromotions.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">You haven't participated in any promotions yet</p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => setActiveTab('available')}
                  >
                    View Available Promotions
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {myPromotions.map((promotion) => {
                    const optIn = myOptIns.find(opt => opt.sale_id === promotion.id);
                    return (
                      <Card key={promotion.id} className="hover:shadow-md transition-shadow border-l-4 border-l-green-500">
                        <CardContent className="pt-6">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-3">
                                <h3 className="text-lg font-semibold">{promotion.name}</h3>
                                <Badge className={getStatusColor(promotion.status)}>
                                  {promotion.status}
                                </Badge>
                                <Badge variant="outline">{promotion.theme}</Badge>
                                <Badge className="bg-green-100 text-green-800">
                                  Participating
                                </Badge>
                              </div>
                              
                              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                  <Calendar className="h-4 w-4" />
                                  <div>
                                    <p className="font-medium">Start Date</p>
                                    <p>{formatDate(promotion.start_timestamp)}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                  <Calendar className="h-4 w-4" />
                                  <div>
                                    <p className="font-medium">End Date</p>
                                    <p>{formatDate(promotion.end_timestamp)}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 text-sm">
                                  <Package className="h-4 w-4 text-green-600" />
                                  <div>
                                    <p className="text-gray-600 font-medium">Products</p>
                                    <p className="font-semibold">{optIn?.total_products || 0}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 text-sm">
                                  <TrendingUp className="h-4 w-4 text-blue-600" />
                                  <div>
                                    <p className="text-gray-600 font-medium">Avg Discount</p>
                                    <p className="font-semibold">{optIn?.discount_offered?.toFixed(1) || 0}%</p>
                                  </div>
                                </div>
                              </div>

                              {optIn && optIn.products_committed && optIn.products_committed.length > 0 && (
                                <div className="bg-gray-50 p-4 rounded-lg">
                                  <p className="text-sm font-medium text-gray-700 mb-2">Committed Products:</p>
                                  <div className="space-y-1">
                                    {optIn.products_committed.slice(0, 3).map((product, idx) => (
                                      <p key={product.id || product.name || `${product.title || "product"}-${idx}`} className="text-sm text-gray-600">
                                        • {product.product_title}: <span className="font-medium">₹{product.committed_price}</span> 
                                        <span className="text-green-600 ml-2">({product.discount_percentage}% off)</span>
                                      </p>
                                    ))}
                                    {optIn.products_committed.length > 3 && (
                                      <p className="text-sm text-blue-600">
                                        + {optIn.products_committed.length - 3} more products
                                      </p>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default VendorPromotions;
